import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

/**
 * The "you cannot have two active webinars" alert box.
 *
 * A blocking dialog rather than the usual snackbar, on purpose: this is a rule
 * the user has to act on (go and deactivate the other webinar) rather than a
 * transient failure they can ignore, and a snackbar that fades after four
 * seconds gets missed on a wide grid.
 *
 * The API is the authority — it refuses the second activation and returns
 * `data.code === 'MULTIPLE_ACTIVE_NOT_ALLOWED'` along with the row standing in
 * the way. `message` here is that server message verbatim, so the wording only
 * ever lives in one place.
 */
export const MULTIPLE_ACTIVE_CODE = 'MULTIPLE_ACTIVE_NOT_ALLOWED';

/** True when a thunk result is the API refusing a second active webinar. */
export const isActiveConflict = (res) => res?.payload?.data?.code === MULTIPLE_ACTIVE_CODE;

/** The name of the webinar already holding the slot, when the API named one. */
export const activeConflictName = (res) => res?.payload?.data?.conflict?.webinar_name || null;

const ActiveConflictAlert = ({ open, message, conflictName, onClose }) => (
  <Dialog open={Boolean(open)} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Cannot activate this webinar</DialogTitle>
    <DialogContent>
      <Alert severity="warning" sx={{ mb: conflictName ? 2 : 0 }}>
        {message || 'Multiple webinars cannot be active at the same time.'}
      </Alert>
      {conflictName && (
        <Typography variant="body2" color="text.secondary">
          Set <strong>{conflictName}</strong> to Inactive first, then activate this one.
        </Typography>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" autoFocus>
        OK
      </Button>
    </DialogActions>
  </Dialog>
);

export default ActiveConflictAlert;
